import React from "react";
import { Box } from "@mui/material";
import { useAuthenticatedUserState } from "recoils";
import { useGetAllowedValues, useSites, useGetConfigParameter, useAllCollectors, useUtilityBillsYears } from "hooks";
import { DashboardTabs } from "components";
import { isPageEnabled } from "utils";
import { TARGETS_PAGE } from "pages";
import { TabName, SubTabName, TabComponent } from "./types";
import { MainTab, DataReadinessTab } from "./components";
import TargetsTab from "./components/tabs/targets";

export const DashboardPageV2 = () => {
  const user = useAuthenticatedUserState();
  // TODO: TEMPORAL - REMOVE "Intensity" from tabs array and tenant check when Intensity external link functionality is removed
  // Only show Intensity tab for "live-colliers-us" tenant
  const shouldShowIntensity = user.tenant === "live-colliers-us" || user.tenant === "wip-testing";

  const { data: allowedValues } = useGetAllowedValues();
  const { data: sitesData } = useSites();
  const { data: configParameter } = useGetConfigParameter();
  const { data: collectorsData } = useAllCollectors();
  const { data: availableYears } = useUtilityBillsYears();

  const isTargetsEnabled = isPageEnabled(TARGETS_PAGE, configParameter);
  const baseTabs: TabName[] = isTargetsEnabled
    ? ["GHG Emissions", "Consumption", "Financials", "Data Readiness", "Targets"]
    : ["GHG Emissions", "Consumption", "Financials", "Data Readiness"];
  const tabs: TabName[] = shouldShowIntensity ? [...baseTabs, "Intensity"] : baseTabs;
  const initialTab: TabName = tabs[0];

  const baseSubTabs: Partial<Record<TabName, SubTabName[]>> = {
    "GHG Emissions": ["Location", "Market"],
    "Consumption": ["Overview", "Electricity", "Natural Gas", "Water", "Sewer"],
    "Financials": ["Overview", "Electricity", "Natural Gas", "Water", "Sewer"],
    "Data Readiness": [],
    "Targets": [],
  };
  // TODO: TEMPORAL - REMOVE "Intensity" entry when Intensity external link functionality is removed
  const subTabs: Partial<Record<TabName, SubTabName[]>> = shouldShowIntensity
    ? { ...baseSubTabs, "Intensity": [] }
    : baseSubTabs;

  const fiscalYears = availableYears ? availableYears && [...availableYears.map((yearItem: any) => `${yearItem}`)] : [];

  const allTabs: TabComponent[] = tabs.map((tab, index) => {
    // TODO: TEMPORAL - REMOVE this entire if block when Intensity external link functionality is removed
    // Intensity is a special case - it's a link, not a tab with content
    if (tab === "Intensity") {
      return {
        label: tab,
        content: null, // Will be handled as a link in DashboardTabs
        isExternalLink: true,
      };
    }

    if (tab === "Data Readiness") {
      return {
        label: tab,
        content: <DataReadinessTab />,
        isExternalLink: false,
      };
    }

    if (tab === "Targets") {
      return {
        label: tab,
        content: <TargetsTab />,
        isExternalLink: false,
      };
    }

    const subTabsForTab = subTabs[tab];
    return {
      label: tab,
      content: <MainTab
        currentMainTab={tabs[index]}
        tabs={subTabsForTab ?? []}
        allowedValues={allowedValues}
        sitesData={sitesData}
        configParameter={configParameter}
        collectorsData={collectorsData}
        fiscalYears={fiscalYears}
      />,
      // TODO: TEMPORAL - REMOVE isExternalLink: false when Intensity external link functionality is removed
      isExternalLink: false,
    };
  })

  return (
    <Box sx={{ ml: 3 }}>
      <DashboardTabs intensity tabs={allTabs} selectedBtnOption={initialTab} dashboard />
    </Box>
  );
}
